import { Component, inject } from '@angular/core';
import { ReturnWarrantyCreateViewModalComponent, Customer, OrderServicesTicket, OrderServicesRow, ConfigurationsService, OrderRow, WarrantySelectionAction, TableRecord, MetadataService } from 'gung-standard';
import { GungUser as User } from 'gung-standard';
import { FormGroup, Validators } from '@angular/forms';
import { TyloOrderServicesService } from '../../services/tylo-order-services.service';
import { OptionsListMulti } from 'gung-common';
import { first, forkJoin, map, switchMap, of, Observable, tap, catchError, startWith, filter, distinctUntilChanged, takeUntil } from 'rxjs';
import { TyloUserServiceService } from '../../services/tylo-user-service.service';

@Component({
  selector: 'tylo-return-warranty-create-view-modal',
  standalone: false,
  templateUrl: './tylo-return-warranty-create-view-modal.component.html',
  styleUrl: './tylo-return-warranty-create-view-modal.component.css'
})
export class TyloReturnWarrantyCreateViewModalComponent extends ReturnWarrantyCreateViewModalComponent {
  protected orderServicesService = inject(TyloOrderServicesService);
  protected configurationService = inject(ConfigurationsService);
  protected metadataService = inject(MetadataService);
  protected tyloUsersService = inject(TyloUserServiceService);

  private orderServiceConfiguration: OrderServiceConfiguration;
  protected selectedUser: string;
  protected userOptions: OptionsListMulti[] = [];
  protected customerWarrantySubReason: OptionsListMulti[] = [];
  protected typeOfUseOptions: { id: string; name: string }[] = [];
  protected searchMode: boolean = false;
  protected termsCheckboxShown: boolean = false;
  protected orderRowsAreEmpty: boolean = false;

  protected newDataAdded: boolean = false;
  protected waitingForInfoSet: boolean = false;
  protected isSaved: boolean = false;

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeTypeOfUseOptions();
    this.fetchConfiguration();
  }

  private initializeTypeOfUseOptions(): void {
    this.typeOfUseOptions = [
      { id: 'Privat', name: this.translationService.instant('PRIVATE') },
      { id: 'Offentlig', name: this.translationService.instant('COMMERCIAL') }
    ];
  }

  private fetchConfiguration(): void {
    this.configurationService
      .getConfigurationsByIds(['TyloOrderServicesConfig'])
      .pipe(
        first(),
        takeUntil(this.unsubscribe)
      )
      .subscribe(configuration => {
        if (!configuration) {
          return;
        }
        this.orderServiceConfiguration = configuration[0];
      });
  }

  override initForm(user: User, customer: Customer, data?: OrderServicesTicket): void {
    super.initForm(user, customer, data);
    this.addExtraMapToForm(user, data);
    this.updateUserOptions();
    if (this.create) {
      this.searchMode = true;
    }
    this.shouldTermsCheckboxBeShown();
    this.setupReturnCauseListener();
    this.setupClaimWorkflowListener();

    if (this.submitForm.get('status')?.value === 'APPROVED') {
      this.fetchRmaNrFromOrderService(this.data.id)
    }

    if (!this.isAdmin) {
      this.submitForm.get('extra.sendAttestTo')?.setValue(user.email);
    }

    this.submitForm.get('date')?.setValue(data?.date || new Date());

    if (this.create) {
      const commentControl = this.submitForm.get('comment');
      if (commentControl) {
        commentControl.setValidators([Validators.required]);
        commentControl.updateValueAndValidity();
      }
    }
  }

  private addExtraMapToForm(user: User, data: OrderServicesTicket): void {
    const matchingRow = data?.rows?.find((r: any) => r.extra?.selected) || data?.rows?.[0];
    this.submitForm.removeControl('extra');
    this.submitForm.addControl(
      'extra',
      new FormGroup({
        productId: this.formBuilder.control(data?.extra?.productId || ''),
        serialNumber: this.formBuilder.control(data?.extra?.serialNumber || ''),
        sendAttestTo: this.formBuilder.control(data?.extra?.sendAttestTo || user.email),
        returnCause: this.formBuilder.control(data?.extra?.returnCause || ''),
        returnCauseGroup: this.formBuilder.control(data?.extra?.returnCauseGroup || ''),
        returnSubCause: this.formBuilder.control(data?.extra?.returnSubCause || ''),
        approveTerms: this.formBuilder.control(data?.extra?.approveTerms || false),
        selected: this.formBuilder.control(data?.extra?.selected || false),
        claimNoCounterPart: this.formBuilder.control(data?.extra?.claimNoCounterPart || ''),
        rmaNr: this.formBuilder.control(data?.extra?.rmaNr || ''),
        claimWorkflow: this.formBuilder.control(data?.extra?.claimWorkflow || ''),
        ordradnr: this.formBuilder.control(data?.extra?.ordradnr || matchingRow?.extra?.rownr || ''),
        faktnr: this.formBuilder.control(data?.extra?.faktnr || matchingRow?.extra?.faktnr || null),
        quantity: this.formBuilder.control(data?.extra?.quantity || matchingRow?.quantity || ''),
        // New mandatory fields
        dateOfSale: this.formBuilder.control(data?.extra?.dateOfSale || new Date(), [Validators.required]),
        dateFaultOccurred: this.formBuilder.control(data?.extra?.dateFaultOccurred || new Date(), [Validators.required]),
        typeOfUse: this.formBuilder.control(data?.extra?.typeOfUse || '', [Validators.required]),
        // non-mandatory address fields
        endCustomerName: this.formBuilder.control(data?.extra?.endCustomerName || ''),
        COAddress: this.formBuilder.control(data?.extra?.COAddress || ''),
        phoneNo: this.formBuilder.control(data?.extra?.phoneNo || ''),
        endCustomerEmail: this.formBuilder.control(data?.extra?.endCustomerEmail || ''),
        endCustomerAddress: this.formBuilder.control(data?.extra?.endCustomerAddress || ''),
        zipcode: this.formBuilder.control(data?.extra?.zipcode || ''),
        city: this.formBuilder.control(data?.extra?.city || ''),
        country: this.formBuilder.control(data?.extra?.country || '')
      })
    );
  }

  protected claimWorkflowFilterFunction = (option: { key: string; value: any }): boolean => {
    const allowedCodes = [10, 30, 40, 600, 700];
    const claimworkflowCode = option.value?.claimworkflow;
    return allowedCodes.includes(claimworkflowCode);
  };

protected searchRows(): void {
    const customerId = this.submitForm.get('customerId')?.value;
    this.searchMode = false;
    if (!customerId) {
      console.error('No customer selected');
      return;
    }

    const productId = this.submitForm.get('extra.productId')?.value;
    const serialNumber = this.submitForm.get('extra.serialNumber')?.value;

    // clear rows before a new search
    while (this.rows.length !== 0) {
      this.rows.removeAt(0);
    }

    if (serialNumber) {
      this.orderServicesService.findOrdersContainingSerialNr(serialNumber, customerId).pipe(
        takeUntil(this.unsubscribe)
      ).subscribe({
        next: (rows) => {
          this.processOrderRows(rows);
          if (this.rows.length > 0) {
            const matchingIndex = this.rows.controls.findIndex(row =>
              row.get('extra.serialNumber')?.value === serialNumber
            );
            if (matchingIndex >= 0) {
              const foundProductId = this.rows.at(matchingIndex).get('id')?.value;
              if (foundProductId) {
                this.submitForm.get('extra.productId')?.setValue(foundProductId);
              }
              this.onRowSelectionChange(matchingIndex);
            }
          } else {
            this.orderRowsAreEmpty = true;
          }
        },
        error: (err) => {
          console.error('Error fetching order rows by serial number', err);
        }
      });
    } else {
      this.orderServicesService.findOrdersContainingProductId(productId, customerId).pipe(
        takeUntil(this.unsubscribe)
      ).subscribe({
        next: (rows) => {
          this.processOrderRows(rows);
          if (this.rows.length === 0) {
            this.orderRowsAreEmpty = true;
          }
        },
        error: (err) => {
          console.error('Error fetching order rows by product id', err);
        }
      });
    }
  }

  private processOrderRows(rows: OrderRow[]): void {
      rows.forEach(row => {
        const orderServicesRow = {
          id: row.productId,
          quantity: row.quantity,
          reasonCode: [],
          attachments: [],
          note: row.orderId || '',
          date: new Date(row.extra.orp.orddatum),
          extra: {
            serialNumber: row.extra.orp.serialNumber,
            selected: false,
            rownr: row.rownr,
            varugruppkod: row.extra.orp.varugruppkod,
            faktnr: row.extra.orp.faktnr?.trim() || null,
            kundbestnr: row.extra.orp.kundbestnr?.trim() || null
          }
        };
        this.addRow(orderServicesRow);
      });
  }

  private fetchRmaNrFromOrderService(orderServiceId : string): void {
    if (!orderServiceId) {
      return;
    }
    this.orderServicesService.findOrderServicesTicketById(orderServiceId).pipe(
      first(),
      takeUntil(this.unsubscribe)
    ).subscribe({
      next: (orderService) => {
        const rmaNr = orderService.extra?.rmaNr;
        if (rmaNr) {
          this.submitForm.get('extra.rmaNr')?.setValue(rmaNr);
        }
      },
      error: (err) => {
        console.error('Error fetching order service details for RMA number', err);
      }
    });
  }

  private isSerialNumberRequiredForProduct(): boolean {
    if (!this.orderServiceConfiguration?.articleGroupsWhereSerialNrSearchIsRequired) {
      return false;
    }

    const articleGroupsWhereSerialNrSearchIsRequired: string[] =
      this.orderServiceConfiguration.articleGroupsWhereSerialNrSearchIsRequired;

    const selectedRow = this.rows.controls.find(row =>
      row.get('extra.selected')?.value === true,
    );

    if (selectedRow) {
      const varugruppkod = selectedRow.get('extra.varugruppkod')?.value;
      if (varugruppkod != null) {
        return articleGroupsWhereSerialNrSearchIsRequired.includes(varugruppkod);
      }
    }

    const productId = this.submitForm.get('extra.productId')?.value;
    if (productId) {
      const matchingRow = this.rows.controls.find(row =>
        row.get('id')?.value === productId
      );

      if (matchingRow) {
        const varugruppkod = matchingRow.get('extra.varugruppkod')?.value;
        if (varugruppkod != null) {
          return articleGroupsWhereSerialNrSearchIsRequired.includes(varugruppkod);
        }
      }
    }

    return false;
  }

  protected setSelectedUser(selected: User): void {
    this.selectedUser = selected.email[0] || undefined;
    this.submitForm.get('extra.sendAttestTo')?.setValue(this.selectedUser);
  }

  protected updateUserOptions(): void {
    forkJoin({
      users: this.tyloUsersService.findUsers().pipe(first()),
      currentUser: this.authService.getCurrentUser().pipe(first())
    }).pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(({ users, currentUser }) => {
      this.currentUser = currentUser;
      const savedSendAttestTo = this.submitForm.get('extra.sendAttestTo')?.value;
      const defaultEmail = savedSendAttestTo || currentUser.email;

      this.userOptions = users.map(u => {
        return {
          id: u.id,
          name: u.name,
          selected: u.email === defaultEmail
        };
      })
    });
  }

  protected setReturnCauseGroup(returnCause: string): void {
    const returnCauseMetadata = this.metadataService.getMetaDataTableMap('orkk') || {};

    const matchingItem = Object.values(returnCauseMetadata).find(
      item => item.kundreklamationorsak?.toString() === returnCause
    );

    if (matchingItem) {
      const returnCauseGroup = matchingItem.kundreklamationorsakgrp;
      this.submitForm.get('extra.returnCauseGroup')?.setValue(returnCauseGroup);
    } else {
      console.log('No matching returnCause found for:', returnCause);
    }
  }

  protected setCustomerWarrantySubReason(selected: string[]): void {
    let selectedSubReason = selected[0] || undefined;
    this.submitForm.get('extra.returnSubCause')?.setValue(selectedSubReason);
  }

  private setupReturnCauseListener(): void {
    const returnCauseControl = this.submitForm.get('extra.returnCause');
    let previousReturnCause: string;

    returnCauseControl?.valueChanges.pipe(
      startWith(returnCauseControl.value),
      filter(returnCause => !!returnCause),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe)
    ).subscribe(returnCause => {
      const causeChanged = previousReturnCause !== undefined;
      previousReturnCause = returnCause;

      this.setReturnCauseGroup(returnCause);

      if (causeChanged) {
        this.resetReturnSubCause();
      } else {
        this.updateOptions(returnCause);
      }
    });
  }

  private setupClaimWorkflowListener(): void {
    const claimWorkflowControl = this.submitForm.get('extra.claimWorkflow');
    let previousClaimWorkflow = claimWorkflowControl?.value;

    claimWorkflowControl?.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.unsubscribe)
    ).subscribe(claimWorkflow => {
      if (claimWorkflow === previousClaimWorkflow) {
        return;
      }
      previousClaimWorkflow = claimWorkflow;

      this.resetReturnSubCause();
    });
  }
  private resetReturnSubCause(): void {
    this.submitForm.get('extra.returnSubCause')?.setValue('');
    this.updateOptions(this.submitForm.get('extra.returnCause')?.value);
  }

  protected updateOptions(returnCause: string): void {
    const customerReturnReason: TableRecord[] = this.metadataService.getMetadataTableList('q_jis_orkkk') || [];
    const savedSubCause = this.submitForm.get('extra.returnSubCause')?.value;

    this.customerWarrantySubReason = customerReturnReason
      .filter(item => item.kundreklamationorsak?.toString() === returnCause)
      .map(item => {
        return {
          id: item.q_jis_subkundreklamationorsak?.toString(),
          name: item.q_jis_subkundreklamationbeskr,
          selected: item.q_jis_subkundreklamationorsak?.toString() === savedSubCause?.toString()
        };
      });
  }

  protected override saveIfCreate(formRaw: any, timestamp: Date): any {
    if (this.create) {
      const result = {
        ...formRaw,
        userName: this.currentUser.name,
        userEmail: this.currentUser.email,
        id: undefined,
        status: 'REGISTERED',
        date: timestamp,
        comments: [
          {
            user: this.currentUser.username,
            date: timestamp,
            attachments: [],
            comment: formRaw.comment
          }
        ]
      };
      return result;
    }
    return undefined;
  }

  override onSave(): void {
    let status = this.submitForm.get('status')?.value;

    if (status === 'WAITING_INFO_FROM_CUSTOMER' && !!this.newDataAdded && !this.waitingForInfoSet) {
      status = 'ADDITIONAL_INFO_PROVIDED';
    }

    this.submitForm.get('status').setValue(status);

    // Check if form is valid before proceeding
    if (this.submitForm.invalid) {
      this.submitForm.markAllAsTouched();
      return;
    }

    let orderId = this.submitForm.get('orderId')?.value;
    let sendAttestTo = this.submitForm.get('extra.sendAttestTo')?.value;
    let productId = this.submitForm.get('extra.productId')?.value;

    this.tyloUsersService.findUsers().pipe(
      first(),
      map(users => {
        const user = users.find(u => u.email === sendAttestTo);
        return user?.id || sendAttestTo;
      }),
      switchMap(id =>
        this.orderServicesService.sendEmailWithAttestLink(sendAttestTo, orderId, status, productId, id)
      ),
      takeUntil(this.unsubscribe)
    ).subscribe({
      next: () => {
        console.log('Attestation email sent successfully');
        this.isSaved = true;
        super.onSave();
      },
      error: (err) => {
        console.error('Error sending attestation email', err);
      }
    });
  }

  override addRow(data?: OrderServicesRow): void {
    super.addRow(data);

    const rowIndex = this.rows.length - 1;
    const rowFormGroup = this.rows.at(rowIndex) as FormGroup;
    if (!rowFormGroup.get('extra')) {
      rowFormGroup.addControl('extra', new FormGroup({}));
    }

    const extraFormGroup = rowFormGroup.get('extra') as FormGroup;
    if (!extraFormGroup.get('serialNumber')) {
      extraFormGroup.addControl(
        'serialNumber',
        this.formBuilder.control(data?.extra?.serialNumber || '')
      );
    }

    if (!extraFormGroup.get('selected')) {
      extraFormGroup.addControl(
        'selected',
        this.formBuilder.control(data?.extra?.selected || false)
      );
    }

    if (data?.extra?.rownr && !extraFormGroup.get('rownr')) {
      extraFormGroup.addControl(
        'rownr',
        this.formBuilder.control(data.extra.rownr)
      );
    }

    if (data?.extra?.varugruppkod && !extraFormGroup.get('varugruppkod')) {
      extraFormGroup.addControl(
        'varugruppkod',
        this.formBuilder.control(data.extra.varugruppkod)
      );
    }

    if (data?.extra?.faktnr && !extraFormGroup.get('faktnr')) {
      extraFormGroup.addControl(
        'faktnr',
        this.formBuilder.control(data.extra.faktnr)
      );
    }

    if (data?.extra?.kundbestnr && !extraFormGroup.get('kundbestnr')) {
      extraFormGroup.addControl(
        'kundbestnr',
        this.formBuilder.control(data.extra.kundbestnr)
      );
    }
  }

  protected onRowSelectionChange(rowIndex: number): void {
    const selectedRowFormGroup = this.rows.at(rowIndex) as FormGroup;
    const currentSelectionValue = selectedRowFormGroup.get('extra.selected')?.value;
    const orderid = selectedRowFormGroup.get('note')?.value;
    const newSelection = !currentSelectionValue;

    this.submitForm.get('orderId')?.setValue(orderid);
    selectedRowFormGroup.get('extra.selected')?.setValue(newSelection, { emitEvent: false });

    if (newSelection) {
      this.rows.controls.forEach((row, index) => {
        if (index !== rowIndex) {
          row.get('extra.selected')?.setValue(false, { emitEvent: false });
        }
      });

      const serialNumber = selectedRowFormGroup.get('extra.serialNumber')?.value || '';
      this.submitForm.get('extra.serialNumber')?.setValue(serialNumber);

      const faktnr = selectedRowFormGroup.get('extra.faktnr')?.value;
      if (faktnr) {
        this.submitForm.get('extra.faktnr')?.setValue(faktnr);
      }

      const quantity = selectedRowFormGroup.get('quantity')?.value;
      if (quantity) {
        this.submitForm.get('extra.quantity')?.setValue(quantity);
      }
      const ordradnr = selectedRowFormGroup.get('extra.rownr')?.value;
      if (ordradnr) {
        this.submitForm.get('extra.ordradnr')?.setValue(ordradnr);
      }
    }
  }

  private shouldTermsCheckboxBeShown(): void {
    if (!this.create) {
      return;
    }

    const customerId = this.submitForm.get('customerId')?.value;
    this.customerService.getCustomer(customerId).pipe(
      first(),
      switchMap((customer) => {
        return this.submitForm.get('extra.returnCause')?.valueChanges.pipe(
          map(returnCauseValue => ({ returnCauseValue, customer }))
        );
      }),
      takeUntil(this.unsubscribe)
    ).subscribe(({ returnCauseValue, customer }) => {
      this.termsCheckboxShown =
        returnCauseValue === "44" &&
        (customer.extra.kus.momskod === 1 || customer.extra.kus.momskod === 7);
    });

  }
  protected override getStatusActions(actions: WarrantySelectionAction<string>[]): void {
    const excludeFromDropdown: string[] = ['REGISTERED', 'ADDITIONAL_INFO_PROVIDED'];
    const currentStatus = this.submitForm.get('status').value;

    if (currentStatus === 'REJECTED' || currentStatus === 'APPROVED') {
      return;
    }

    for (const status of (this.claimStatusMetadata ?? []).filter((s: TableRecord) => !excludeFromDropdown.includes(s.name))) {
      actions.push({
        label: this.translationService.instant('STATUS') + ' ' + this.translationService.instant(status.name || 'UNDEFINED'),
        performAction: (selection: any) => {
          if (status.name === 'APPROVED' || status.name === 'REJECTED') {
            this.submitForm.get('status').setValue(status.name);
            this.data.status = status.name;
          } else {
            this.data.status = status.name;
            this.orderServicesService.postOrderServices(this.data).pipe(
              takeUntil(this.unsubscribe)
            ).subscribe(data => {
              this.data = data;
              this.submitForm.get('status').setValue(data.status);
              this.waitingForInfoSet = true;
            });
          }
          return of();
        }
      });
    }
  }

  protected get serialNrIsRequired(): boolean {
    if (!this.orderServiceConfiguration?.articleGroupsWhereSerialNrSearchIsRequired) {
      return false;
    }

    const serialNumber = this.submitForm.get('extra.serialNumber')?.value;
    if (serialNumber && serialNumber.trim() !== '') {
      return false;
    }

    const articleGroupsWhereSerialNrSearchIsRequired: string[] = this.orderServiceConfiguration.articleGroupsWhereSerialNrSearchIsRequired;

    const selectedRow = this.rows.controls.find(row =>
      row.get('extra.selected')?.value === true
    );

    if (selectedRow) {
      const varugruppkod = selectedRow.get('extra.varugruppkod')?.value;
      if (varugruppkod != null) {
        return articleGroupsWhereSerialNrSearchIsRequired.includes(varugruppkod);
      }
    }

    const productId = this.submitForm.get('extra.productId')?.value;
    if (productId) {
      const matchingRow = this.rows.controls.find(row =>
        row.get('id')?.value === productId
      );

      if (matchingRow) {
        const varugruppkod = matchingRow.get('extra.varugruppkod')?.value;
        if (varugruppkod != null) {
          return articleGroupsWhereSerialNrSearchIsRequired.includes(varugruppkod);
        }
      }
    }

    return false;
  }

  protected get shouldSearchButtonBeDisabled(): boolean {
    const productId = this.submitForm.get('extra.productId')?.value;
    const serialNumber = this.submitForm.get('extra.serialNumber')?.value;

    return !productId && !serialNumber;
  }

  protected get isSaveButtonDisabled(): boolean {
    if (!this.submitForm || !this.submitForm.get('extra')) {
      return true;
    }

    const serialNumber = this.submitForm.get('extra.serialNumber')?.value;
    const approveTerms = this.submitForm.get('extra.approveTerms')?.value;
    const productId = this.submitForm.get('extra.productId')?.value;
    const date = this.submitForm.get('date')?.value;
    const returnCause = this.submitForm.get('extra.returnCause')?.value;
    const sendAttestTo = this.submitForm.get('extra.sendAttestTo')?.value;
    const customer = this.submitForm.get('customerId')?.value;
    const comment = this.submitForm.get('comment')?.value;
    const dateOfSale = this.submitForm.get('extra.dateOfSale')?.value;
    const dateFaultOccurred = this.submitForm.get('extra.dateFaultOccurred')?.value;
    const typeOfUse = this.submitForm.get('extra.typeOfUse')?.value;

    // Check if comment is empty or only whitespace
    if (this.create && (!comment || comment.trim() === '')) {
      return true;
    }

    if (this.isApproved && (this.isClaimWorkflowRequired || this.isReturnSubCauseRequired || this.isReturnCauseGroupRequired)) {
      return true;
    }

    const orderId = this.submitForm.get('orderId')?.value;

    if (!customer || !productId || productId.trim() === '' || !date || !returnCause || returnCause.trim() === '' || !sendAttestTo || sendAttestTo.trim() === '') {
      return true;
    }

    if (this.create && (!orderId || orderId.trim() === '')) {
      return true;
    }

    // Check mandatory new fields
    if (!dateOfSale || !dateFaultOccurred || !typeOfUse || typeOfUse.trim() === '') {
      return true;
    }

    const needsSerialNumber = this.isSerialNumberRequiredForProduct();

    if (needsSerialNumber) {
      if (!serialNumber || serialNumber.trim() === '') {
        return true;
      }
    }

    if (this.termsCheckboxShown) {
      return !approveTerms;
    }

    return false;
  }

  protected get isOrderIdRequired(): boolean {
    if (!this.create) {
      return false;
    }
    const orderId = this.submitForm.get('orderId')?.value;
    return !orderId || orderId.trim() === '';
  }

  protected get isCustomerRequired(): boolean {
    return !this.submitForm.get('customerId')?.value;
  }

  protected get isProductIdRequired(): boolean {
    const productId = this.submitForm.get('extra.productId')?.value;
    return !productId || productId.trim() === '';
  }

  protected get isDateRequired(): boolean {
    return !this.submitForm.get('date')?.value;
  }
  protected get isReturnCauseRequired(): boolean {
    const returnCause = this.submitForm.get('extra.returnCause')?.value;
    return !returnCause || returnCause.trim() === '';
  }

  protected get isCommentRequired(): boolean {
    if (!this.create) {
      return false;
    }
    const comment = this.submitForm.get('comment')?.value;
    return !comment || comment.trim() === '';
  }

  protected get isApproved(): boolean {
    return this.submitForm.get('status')?.value === 'APPROVED';
  }

  private isApprovalFieldMissing(path: string): boolean {
    if (!this.isApproved) {
      return false;
    }
    const value = this.submitForm.get(path)?.value;
    return value === null || value === undefined || value.toString().trim() === '';
  }

  protected get isClaimWorkflowRequired(): boolean {
    return this.isApprovalFieldMissing('extra.claimWorkflow');
  }

  protected get isReturnSubCauseRequired(): boolean {
    return this.isApprovalFieldMissing('extra.returnSubCause');
  }

  protected get isReturnCauseGroupRequired(): boolean {
    return this.isApprovalFieldMissing('extra.returnCauseGroup');
  }

  protected get isDateOfSaleRequired(): boolean {
    return !this.submitForm.get('extra.dateOfSale')?.value;
  }

  protected get isDateFaultOccurredRequired(): boolean {
    return !this.submitForm.get('extra.dateFaultOccurred')?.value;
  }

  protected get isTypeOfUseRequired(): boolean {
    const typeOfUse = this.submitForm.get('extra.typeOfUse')?.value;
    return !typeOfUse || typeOfUse.trim() === '';
  }

}

interface OrderServiceConfiguration {
  articleGroupsWhereSerialNrSearchIsRequired: string[];
}
